import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IconCheck, IconDownload, IconFile, IconUpload, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { JobStatus } from '../../components/JobStatus';
import { useJobStatus } from '../../hooks/useJobStatus';
import { documentApiConnect } from '../../lib/api/documentApi.connect';
const MAX_FILE_SIZE = 250 * 1024 * 1024; // 250MB
function validateFile(file) {
    if (file.size > MAX_FILE_SIZE) {
        return `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 250MB limit`;
    }
    return null;
}
export default function DocumentUpload() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [options, setOptions] = useState({
        output_format: 'markdown',
        enable_ocr: false,
        use_deep_processing: false,
        extract_tables: true,
        extract_images: false,
        preserve_layout: true,
        chunk_size: 1000,
        overlap_size: 100
    });
    const [uploading, setUploading] = useState(false);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    // Poll job status when response has a job_id
    const { status: jobStatus, isPolling } = useJobStatus(response?.job_id, {
        enabled: !!response?.job_id,
        fetcher: (jobId)=>documentApiConnect.getJobStatus(jobId),
        onComplete: ()=>{
        // Job completed — user can now download
        },
        onError: (error)=>{
            setError(typeof error === 'string' ? error : error.message);
        }
    });
    const handleFileChange = (e)=>{
        const file = e.target.files?.[0];
        if (file) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
            setResponse(null);
            setError(null);
        }
    };
    const handleDrop = (e)=>{
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
            setResponse(null);
            setError(null);
        }
    };
    const handleDragOver = (e)=>{
        e.preventDefault();
    };
    const handleUpload = async ()=>{
        if (!selectedFile) return;
        setUploading(true);
        setError(null);
        setResponse(null);
        try {
            const apiOptions = {
                outputFormat: options.output_format,
                useDeepProcessing: options.use_deep_processing,
                extractTables: options.extract_tables,
                extractImages: options.extract_images,
                preserveLayout: options.preserve_layout,
                chunkSize: options.chunk_size,
                overlapSize: options.overlap_size
            };
            const result = await documentApiConnect.processDocument(selectedFile, apiOptions);
            const data = {
                id: result.id,
                status: result.status,
                filename: result.filename,
                job_id: result.jobId,
                message: result.message
            };
            setResponse(data);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred');
            }
        } finally{
            setUploading(false);
        }
    };
    const handleDownload = async ()=>{
        if (!response?.job_id) return;
        setError(null);
        try {
            const result = await documentApiConnect.downloadDocument(response.job_id);
            const url = window.URL.createObjectURL(result.blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', result.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Failed to download document');
            }
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Helmet, {
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "Document Upload - GovCon AI"
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "description",
                        content: "Upload and process documents with AI"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-6",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-4xl mx-auto space-y-6",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3 mb-6",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "bg-indigo-500 rounded-lg p-2",
                                    children: /*#__PURE__*/ _jsx(IconFile, {
                                        className: "w-6 h-6 text-white"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h1", {
                                            className: "text-2xl font-semibold text-gray-900 dark:text-white",
                                            children: "Document Processing"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-500 dark:text-gray-400",
                                            children: "Upload and process documents with AI"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-lg font-semibold text-gray-900 dark:text-white mb-4",
                                    children: "Upload Document"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    role: "button",
                                    tabIndex: 0,
                                    onDrop: handleDrop,
                                    onDragOver: handleDragOver,
                                    onKeyDown: (e)=>{
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            document.getElementById('file-upload')?.click();
                                        }
                                    },
                                    className: "border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors cursor-pointer",
                                    children: [
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "file",
                                            id: "file-upload",
                                            className: "hidden",
                                            onChange: handleFileChange,
                                            accept: ".pdf,.docx,.doc,.txt,.md,.pptx"
                                        }),
                                        /*#__PURE__*/ _jsxs("label", {
                                            htmlFor: "file-upload",
                                            className: "cursor-pointer flex flex-col items-center",
                                            children: [
                                                /*#__PURE__*/ _jsx(IconUpload, {
                                                    className: "w-12 h-12 text-gray-400 mb-3"
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-gray-700 dark:text-gray-300 font-medium mb-1",
                                                    children: selectedFile ? selectedFile.name : 'Drop your document here or click to browse'
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm text-gray-500 dark:text-gray-400",
                                                    children: "Supports PDF, DOCX, PPTX, TXT, MD files (max 250MB)"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        selectedFile && /*#__PURE__*/ _jsxs("div", {
                            className: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-lg font-semibold text-gray-900 dark:text-white mb-4",
                                    children: "Processing Options"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-1 md:grid-cols-2 gap-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("label", {
                                                    htmlFor: "output-format",
                                                    className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
                                                    children: "Output Format"
                                                }),
                                                /*#__PURE__*/ _jsxs("select", {
                                                    id: "output-format",
                                                    value: options.output_format,
                                                    onChange: (e)=>setOptions({
                                                            ...options,
                                                            output_format: e.target.value
                                                        }),
                                                    className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("option", {
                                                            value: "markdown",
                                                            children: "Markdown"
                                                        }),
                                                        /*#__PURE__*/ _jsx("option", {
                                                            value: "text",
                                                            children: "Plain Text"
                                                        }),
                                                        /*#__PURE__*/ _jsx("option", {
                                                            value: "json",
                                                            children: "JSON"
                                                        }),
                                                        /*#__PURE__*/ _jsx("option", {
                                                            value: "html",
                                                            children: "HTML"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("label", {
                                                    htmlFor: "chunk-size",
                                                    className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
                                                    children: "Chunk Size (words)"
                                                }),
                                                /*#__PURE__*/ _jsx("input", {
                                                    id: "chunk-size",
                                                    type: "number",
                                                    value: options.chunk_size,
                                                    onChange: (e)=>setOptions({
                                                            ...options,
                                                            chunk_size: Number.parseInt(e.target.value)
                                                        }),
                                                    className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                                    min: "100",
                                                    max: "5000"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("label", {
                                                    htmlFor: "overlap-size",
                                                    className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
                                                    children: "Overlap Size (words)"
                                                }),
                                                /*#__PURE__*/ _jsx("input", {
                                                    id: "overlap-size",
                                                    type: "number",
                                                    value: options.overlap_size,
                                                    onChange: (e)=>setOptions({
                                                            ...options,
                                                            overlap_size: Number.parseInt(e.target.value)
                                                        }),
                                                    className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                                    min: "0",
                                                    max: "1000"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-col gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsxs("label", {
                                                    className: "flex items-center gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("input", {
                                                            type: "checkbox",
                                                            checked: options.use_deep_processing,
                                                            onChange: (e)=>setOptions({
                                                                    ...options,
                                                                    use_deep_processing: e.target.checked
                                                                }),
                                                            className: "w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm text-gray-700 dark:text-gray-300",
                                                            children: "Use Deep Processing"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-xs text-gray-500 dark:text-gray-400",
                                                            children: "(slower, better for scanned PDFs)"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("label", {
                                                    className: "flex items-center gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("input", {
                                                            type: "checkbox",
                                                            checked: options.extract_tables,
                                                            onChange: (e)=>setOptions({
                                                                    ...options,
                                                                    extract_tables: e.target.checked
                                                                }),
                                                            className: "w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm text-gray-700 dark:text-gray-300",
                                                            children: "Extract Tables"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("label", {
                                                    className: "flex items-center gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("input", {
                                                            type: "checkbox",
                                                            checked: options.extract_images,
                                                            onChange: (e)=>setOptions({
                                                                    ...options,
                                                                    extract_images: e.target.checked
                                                                }),
                                                            className: "w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm text-gray-700 dark:text-gray-300",
                                                            children: "Extract Images"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("label", {
                                                    className: "flex items-center gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("input", {
                                                            type: "checkbox",
                                                            checked: options.preserve_layout,
                                                            onChange: (e)=>setOptions({
                                                                    ...options,
                                                                    preserve_layout: e.target.checked
                                                                }),
                                                            className: "w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm text-gray-700 dark:text-gray-300",
                                                            children: "Preserve Layout"
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-6",
                                    children: /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: handleUpload,
                                        disabled: uploading,
                                        className: "w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium",
                                        children: uploading ? 'Processing...' : 'Process Document'
                                    })
                                })
                            ]
                        }),
                        error && /*#__PURE__*/ _jsxs("div", {
                            className: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3",
                            children: [
                                /*#__PURE__*/ _jsx(IconX, {
                                    className: "w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "font-medium text-red-900 dark:text-red-200",
                                            children: "Processing Error"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-red-700 dark:text-red-300 mt-1",
                                            children: error
                                        })
                                    ]
                                })
                            ]
                        }),
                        response && /*#__PURE__*/ _jsxs("div", {
                            className: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between mb-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("h2", {
                                            className: "text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(IconCheck, {
                                                    className: "w-6 h-6 text-green-500"
                                                }),
                                                isPolling ? 'Processing Document' : jobStatus === 'completed' ? 'Processing Complete' : 'Upload Complete'
                                            ]
                                        }),
                                        response.job_id && jobStatus === 'completed' && /*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            onClick: handleDownload,
                                            className: "flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors",
                                            children: [
                                                /*#__PURE__*/ _jsx(IconDownload, {
                                                    className: "w-4 h-4"
                                                }),
                                                "Download"
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4",
                                    children: /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-blue-800 dark:text-blue-200",
                                        children: response.message
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "font-medium text-gray-900 dark:text-white mb-2",
                                                    children: "Document Info"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2 text-sm",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex justify-between",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-gray-600 dark:text-gray-400",
                                                                    children: "Filename:"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-medium text-gray-900 dark:text-white",
                                                                    children: response.filename
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex justify-between",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-gray-600 dark:text-gray-400",
                                                                    children: "Attachment ID:"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-mono text-xs text-gray-900 dark:text-white",
                                                                    children: response.id
                                                                })
                                                            ]
                                                        }),
                                                        response.job_id && /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex justify-between",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-gray-600 dark:text-gray-400",
                                                                    children: "Job ID:"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-mono text-xs text-gray-900 dark:text-white",
                                                                    children: response.job_id
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        response.job_id && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "font-medium text-gray-900 dark:text-white mb-2",
                                                    children: "Processing Status"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "bg-gray-50 dark:bg-gray-700 rounded-lg p-4",
                                                    children: /*#__PURE__*/ _jsx(JobStatus, {
                                                        jobId: response.job_id,
                                                        enabled: true,
                                                        showCancel: true,
                                                        fetcher: (jobId)=>documentApiConnect.getJobStatus(jobId),
                                                        onComplete: ()=>{},
                                                        onError: (err)=>{
                                                            setError(typeof err === 'string' ? err : err.message);
                                                        }
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
